import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  slug: "", name: "", specialty: "", bio: "", location: "", phone: "",
  photo_url: "", services: "",
  doctor_email: "", doctor_name: "", doctor_password: "",
};

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const { data } = await api.get("/tenants");
    setTenants(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tenants", {
        ...form,
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Doctor tenant created");
      setOpen(false); setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="tenants-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Doctor Tenants</h1>
          <p className="text-sm text-muted-foreground mt-1">Each tenant = one doctor's private practice with full data isolation.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="new-tenant-btn"><Plus className="size-4" /> New tenant</Button></DialogTrigger>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create doctor tenant</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Tenant slug *</Label>
                  <Input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="dr-jane-doe" data-testid="tenant-slug" />
                  <div className="text-[10px] text-muted-foreground">URL: /d/{form.slug || "your-slug"}</div></div>
                <div className="space-y-1.5"><Label>Display name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. Jane Doe" data-testid="tenant-name" /></div>
              </div>
              <div className="space-y-1.5"><Label>Specialty</Label>
                <Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Bio</Label>
                <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} /></div>
              <div className="space-y-1.5"><Label>Services (comma-separated)</Label>
                <Input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} placeholder="Consultation, ECG, Echo" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Photo URL</Label>
                <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." /></div>
              <div className="border-t border-border pt-3 mt-3">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-3">Initial doctor login</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Doctor name *</Label>
                    <Input required value={form.doctor_name} onChange={(e) => setForm({ ...form, doctor_name: e.target.value })} data-testid="tenant-doctor-name" /></div>
                  <div className="space-y-1.5"><Label>Doctor email *</Label>
                    <Input type="email" required value={form.doctor_email} onChange={(e) => setForm({ ...form, doctor_email: e.target.value })} data-testid="tenant-doctor-email" /></div>
                </div>
                <div className="space-y-1.5 mt-3"><Label>Initial password *</Label>
                  <Input required value={form.doctor_password} onChange={(e) => setForm({ ...form, doctor_password: e.target.value })} data-testid="tenant-doctor-password" /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-tenant-btn">Create tenant</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tenants.map((t) => {
          const publicUrl = `${window.location.origin}/d/${t.slug}`;
          return (
            <Card key={t.id} data-testid={`tenant-${t.slug}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-lg truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t.specialty}</div>
                  </div>
                  <Badge variant="outline">{t.accept_bookings ? "Open" : "Closed"}</Badge>
                </div>
                {t.bio && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{t.bio}</p>}
                <div className="mt-4 p-2.5 bg-muted/40 rounded-md flex items-center gap-2">
                  <ExternalLink className="size-3.5 text-muted-foreground shrink-0" />
                  <span className="mono text-xs flex-1 truncate">{publicUrl}</span>
                  <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }}
                    className="text-muted-foreground hover:text-foreground" data-testid={`copy-${t.slug}`}>
                    <Copy className="size-3.5" />
                  </button>
                  <a href={publicUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">Open</a>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {tenants.length === 0 && (
          <Card className="md:col-span-2"><CardContent className="p-10 text-center text-muted-foreground">No tenants yet. Create the first doctor's practice.</CardContent></Card>
        )}
      </div>
    </div>
  );
}
